// WWI shell stack — a neat pyramid of large artillery shells lying
// horizontally on two timber skids: a 4-3-2 nest of olive cylinder bodies
// with brass cone noses, all pointing the same way.
import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { box } = styleV3;

const OLIVE = "#5f6b3c";
const BRASS = "#b08d3a";
const TIMBER = "#8b6438";

const R = 0.055;        // shell radius
const BODY_L = 0.4;     // cylinder body length
const NOSE_L = 0.13;    // cone nose length
const SKID_H = 0.06;    // timber skid height
const SPACING = 0.118;  // centre-to-centre within a row
// Rows nest into the grooves of the row below.
const ROW_DY = Math.sqrt((2 * R) ** 2 - (SPACING / 2) ** 2);

// One shell lying along +x: olive body plus a brass nose cone at the +x end.
function shell(y: number, z: number): Part[] {
  return [
    {
      geometry: new THREE.CylinderGeometry(R, R, BODY_L, 9),
      color: OLIVE,
      position: [-NOSE_L / 2, y, z],
      rotation: [0, 0, Math.PI / 2],
    },
    {
      geometry: new THREE.ConeGeometry(R * 0.92, NOSE_L, 9),
      color: BRASS,
      // Body centre sits at -NOSE_L/2, so its +x end is at (BODY_L - NOSE_L)/2.
      position: [(BODY_L - NOSE_L) / 2 + NOSE_L / 2, y, z],
      rotation: [0, 0, -Math.PI / 2],
    },
  ];
}

// A row of `n` shells centred on z = 0 at height `y`.
function row(n: number, y: number): Part[] {
  const out: Part[] = [];
  for (let i = 0; i < n; i++) out.push(...shell(y, (i - (n - 1) / 2) * SPACING));
  return out;
}

export const shellStack = defineModel({
  id: "shell-stack",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Shell Stack",
  tags: ["prop", "wwi", "military", "ammunition", "artillery"],
  summary: "A pyramid of artillery shells stacked horizontally on two timber skids.",
  author: "Fable 5",
  footprint: 0.38,
  parts(): Part[] {
    const y0 = SKID_H + R;
    return [
      // Two timber skids running under the shell bodies, across the row.
      box(0.09, SKID_H, 0.56, TIMBER, [-0.19, SKID_H / 2, 0]),
      box(0.09, SKID_H, 0.56, TIMBER, [0.13, SKID_H / 2, 0]),
      ...row(4, y0),
      ...row(3, y0 + ROW_DY),
      ...row(2, y0 + 2 * ROW_DY),
    ];
  },
  animations: [],
});
